import React from 'react';
import { Keyboard, Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import Item from '@/components/sections/news-activities/item';
import { ButtonIcon } from '@/components/shared/buttons/button-icon';
import { Divider } from '@/components/shared/element/divider';
import { HeadingLevel2 } from '@/components/shared/headline/heading-level-2';
import { Container } from '@/components/shared/layout/container';
import { Section } from '@/components/shared/layout/section';
import { styled } from '@/stitches.config';
import { INewsItem } from '@/types/news/news';
import { IButton } from '@/types/shared';

const SlideContainer = styled('div', {
  position: 'relative',
  '.swiper': {
    paddingBottom: 1,
    px: '$space-6',
    '@sm': {
      px: '$space-12',
    },
    '@lg': {
      px: '$space-0',
    },
    '@maxxl': {
      overflow: 'unset',
    },
  },
  '.swiper-slide': {
    '@maxxl': {
      width: 380,
      maxWidth: 380,
    },
    '@maxxs': {
      width: 'calc(100% - 16px) !important',
      maxWidth: 'none',
    },
    '&.one-item': {
      '@maxxs': {
        width: '100%! important',
        maxWidth: '100%',
      },
    },
  },
});

const ButtonIconWrap = styled('div', {
  '@max2xl': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    columnGap: '$space-4',
    marginTop: '$space-8',
  },
  '@2xl': {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: 0,
    paddingTop: `${(213 / 1232) * 100 + '%'}`,
    '& button': {
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-50%)',
      '&.button-prev': {
        right: '100%',
        marginRight: '$space-4',
      },
      '&.button-next': {
        left: '100%',
        marginLeft: '$space-4',
      },
      '&.swiper-button-disabled': {
        opacity: '0',
      },
    },
  },
});
export interface NewsRelatedProps {
  dataTestSection?: string;
  dataTest?: string;
  dataTrack?: string;
  lead?: string;
  title: string;
  buttons?: IButton[];
  items?: INewsItem[];
}

const NewsRelatedSection: React.FC<NewsRelatedProps> = ({
  items = [],
  dataTestSection = 'section_relatedNews',
  dataTest = 'relatedNews',
  dataTrack = 'news-related-item',
  ...props
}) => {
  return (
    <Section test={dataTestSection}>
      <Container>
        <Divider />
      </Container>
      <Section as="div" size="lg" test="news_activities_related">
        <HeadingLevel2
          lead={props.lead}
          title={props.title}
          buttons={props.buttons}
          hideButtonOnMobile
        />
        {items.length > 0 && (
          <Container size="desktop-only">
            <SlideContainer data-test={`${dataTest}_items`}>
              <Swiper
                modules={[Keyboard, Navigation]}
                keyboard
                speed={1000}
                slidesPerView={'auto'}
                spaceBetween={16}
                loop={false}
                navigation={{
                  prevEl: '.button-prev',
                  nextEl: '.button-next',
                }}
                breakpoints={{
                  '480': {
                    slidesPerView: 'auto',
                    spaceBetween: 24,
                  },
                  '640': {
                    slidesPerView: 'auto',
                    spaceBetween: 32,
                  },
                  '768': {
                    slidesPerView: 2,
                    spaceBetween: 32,
                  },
                  '1024': {
                    slidesPerView: 'auto',
                    spaceBetween: 32,
                  },
                  '1280': {
                    slidesPerView: 3,
                    spaceBetween: 48,
                  },
                }}
              >
                {items.map((o, i) => (
                  <SwiperSlide
                    key={i}
                    className={items.length === 1 ? 'one-item' : undefined}
                  >
                    <Item
                      {...o}
                      dataTrack={dataTrack}
                      dataTest={`${dataTest}_item${i + 1}`}
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
              {items.length > 1 && (
                <ButtonIconWrap>
                  <ButtonIcon
                    icon="arrow-back"
                    variant="button-icon-color-gray-dark"
                    size="md"
                    border
                    className="button-prev"
                    test={{
                      dataTest: `${dataTest}_button_previous`,
                    }}
                  />
                  <ButtonIcon
                    icon="arrow-forward"
                    variant="button-icon-color-gray-dark"
                    size="md"
                    border
                    className="button-next"
                    test={{
                      dataTest: `${dataTest}_button_next`,
                    }}
                  />
                </ButtonIconWrap>
              )}
            </SlideContainer>
          </Container>
        )}
        <HeadingLevel2
          lead={props.lead}
          title={props.title}
          buttons={props.buttons}
          hideLeadAndTitle
        />
      </Section>
    </Section>
  );
};
NewsRelatedSection.displayName = 'NewsRelatedSection';
export default NewsRelatedSection;
